import React, { useState } from 'react'
import { usePage } from '@inertiajs/react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, Alert, CircularProgress,
} from '@mui/material'

export default function ContactFormDialog({ open, onClose, onContactCreated }) {
  const { props } = usePage()
  const tenant = props.tenant || {}

  const [data, setData] = useState({
    salutation: '',
    title: '',
    name1: '',
    name2: '',
    email: '',
    phone: '',
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const resetForm = () => {
    setData({ salutation: '', title: '', name1: '', name2: '', email: '', phone: '' })
    setErrors({})
    setSubmitting(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setErrors({})

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/m/${tenant.slug}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          contact: {
            contact_type: 'Person',
            salutation: data.salutation,
            title: data.title,
            name1: data.name1,
            name2: data.name2,
            email: data.email,
            phone: data.phone,
          },
        }),
      })

      if (res.ok) {
        const result = await res.json()
        resetForm()
        onContactCreated(result.contact)
      } else {
        const result = await res.json()
        setErrors(result.errors || {})
      }
    } catch {
      setErrors({ base: ['Netzwerkfehler beim Anlegen des Kontakts.'] })
    } finally {
      setSubmitting(false)
    }
  }

  const hasErrors = Object.keys(errors).length > 0

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="contact-form-dialog"
    >
      <DialogTitle>Neuen Kontakt anlegen</DialogTitle>
      <DialogContent>
        {hasErrors && errors.base && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="dialog-error">
            {errors.base.join(', ')}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              select
              label="Anrede"
              value={data.salutation}
              onChange={(e) => setData({ ...data, salutation: e.target.value })}
              fullWidth
              size="small"
              required
              error={!!errors.salutation}
              helperText={errors.salutation?.[0]}
              data-testid="dialog-salutation"
            >
              <MenuItem value="herr">Herr</MenuItem>
              <MenuItem value="frau">Frau</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              select
              label="Titel"
              value={data.title}
              onChange={(e) => setData({ ...data, title: e.target.value })}
              fullWidth
              size="small"
              data-testid="dialog-title"
            >
              <MenuItem value="">Kein Titel</MenuItem>
              <MenuItem value="dr">Dr.</MenuItem>
              <MenuItem value="prof">Prof.</MenuItem>
              <MenuItem value="prof_dr">Prof. Dr.</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Vorname"
              value={data.name2}
              onChange={(e) => setData({ ...data, name2: e.target.value })}
              fullWidth
              size="small"
              required
              error={!!errors.name2}
              helperText={errors.name2?.[0]}
              data-testid="dialog-name2"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Nachname"
              value={data.name1}
              onChange={(e) => setData({ ...data, name1: e.target.value })}
              fullWidth
              size="small"
              required
              error={!!errors.name1}
              helperText={errors.name1?.[0]}
              data-testid="dialog-name1"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="E-Mail"
              type="email"
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              fullWidth
              size="small"
              data-testid="dialog-email"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Telefon"
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              fullWidth
              size="small"
              data-testid="dialog-phone"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="dialog-cancel">
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="dialog-save"
        >
          {submitting ? <CircularProgress size={20} /> : 'Anlegen'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
